import { useCallback, useEffect, useState } from 'react';
import { api, Match, AdminMember } from './adminApi';
import { inputCls, btnPrimary, btnGhost, cardCls } from './AdminPanel';

const STATUSES = ['upcoming', 'live', 'awaiting_report', 'completed', 'cancelled'];

export default function AdminMatches({ onToast }: { onToast: (msg: string) => void }) {
  const [matches, setMatches] = useState<Match[]>([]);
  const [members, setMembers] = useState<AdminMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Match | null>(null);

  const load = useCallback(async () => {
    const [mRes, mmRes] = await Promise.all([api<{ matches: Match[] }>('/api/admin/matches'), api<{ members: AdminMember[] }>('/api/admin/members')]);
    if (mRes.ok && mRes.data) setMatches(mRes.data.matches);
    if (mmRes.ok && mmRes.data) setMembers(mmRes.data.members);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (match: Match) => {
    if (!window.confirm(`Delete match "${match.title}"? This cannot be undone.`)) return;
    const res = await api(`/api/admin/matches/${match.id}`, { method: 'DELETE' });
    if (res.ok) { onToast('Match deleted'); await load(); } else onToast(res.error || 'Delete failed');
  };

  if (loading) return <p className="text-sm text-gray-500">Loading matches…</p>;

  return (
    <div className={cardCls}>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-bold">Matches ({matches.length})</h2>
        <div className="flex gap-2">
          <button onClick={load} className={btnGhost}>↻ Refresh</button>
          <button onClick={() => setCreating(true)} className={btnPrimary}>+ New match</button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-white/10 text-xs uppercase tracking-wide text-gray-500">
              <th className="py-2 pr-3">Title</th><th className="py-2 pr-3">Date</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Result</th><th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {matches.map((m) => (
              <tr key={m.id} className="border-b border-white/5 hover:bg-white/5">
                <td className="py-2 pr-3"><b>{m.title}</b><div className="text-xs text-gray-500">{m.venueName} · {m.matchType}</div></td>
                <td className="py-2 pr-3">{m.date} {m.startTime}</td>
                <td className="py-2 pr-3"><StatusBadge status={m.status} /></td>
                <td className="py-2 pr-3">{m.result ? `${m.result.teamAScore} – ${m.result.teamBScore}` : '—'}</td>
                <td className="py-2">
                  <div className="flex gap-2">
                    <button onClick={() => setEditing(m)} className={btnGhost}>Edit</button>
                    <button onClick={() => remove(m)} className="rounded-lg border border-red-500/30 px-3 py-1.5 text-sm font-semibold text-red-400 hover:bg-red-500/10">Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && <CreateMatchModal members={members} onClose={() => setCreating(false)} onDone={async () => { setCreating(false); await load(); }} onToast={onToast} />}
      {editing && <EditMatchModal match={editing} members={members} onClose={() => setEditing(null)} onDone={async () => { setEditing(null); await load(); }} onToast={onToast} />}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const color = status === 'completed' ? 'text-emerald-400' : status === 'cancelled' ? 'text-red-400' : status === 'live' ? 'text-orange-400' : status === 'awaiting_report' ? 'text-amber-400' : 'text-sky-400';
  return <span className={color}>{status.replace('_', ' ')}</span>;
}

export function statusCls(status: string) {
  return status === 'completed' ? 'text-emerald-400' : status === 'cancelled' ? 'text-red-400' : status === 'live' ? 'text-orange-400' : status === 'awaiting_report' ? 'text-amber-400' : 'text-sky-400';
}

function CreateMatchModal({ members, onClose, onDone, onToast }: { members: AdminMember[]; onClose: () => void; onDone: () => void; onToast: (m: string) => void }) {
  const [form, setForm] = useState({ title: '', date: new Date().toISOString().slice(0, 10), startTime: '21:00', durationMinutes: '60', matchType: '7v7', maxPlayers: '14', minPlayers: '10', venueName: '', venueAddress: '', totalTurfFee: '0', guestFee: '0' });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    const res = await api('/api/admin/matches', {
      method: 'POST',
      body: JSON.stringify({ ...form, durationMinutes: Number(form.durationMinutes), maxPlayers: Number(form.maxPlayers), minPlayers: Number(form.minPlayers), totalTurfFee: Number(form.totalTurfFee), guestFee: Number(form.guestFee) }),
    });
    setBusy(false);
    if (res.ok) { onToast('Match created'); onDone(); } else onToast(res.error || 'Create failed');
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#12181f] p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between"><h3 className="text-lg font-black">New match</h3><button onClick={onClose} className="text-gray-500 hover:text-white">×</button></div>
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 block"><span className="text-xs text-gray-500">Title</span><input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
          <label className="block"><span className="text-xs text-gray-500">Date</span><input className={inputCls} type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></label>
          <label className="block"><span className="text-xs text-gray-500">Start time</span><input className={inputCls} type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} /></label>
          <label className="block"><span className="text-xs text-gray-500">Duration (min)</span><input className={inputCls} type="number" value={form.durationMinutes} onChange={(e) => setForm({ ...form, durationMinutes: e.target.value })} /></label>
          <label className="block"><span className="text-xs text-gray-500">Format</span>
            <select className={inputCls} value={form.matchType} onChange={(e) => setForm({ ...form, matchType: e.target.value })}>
              {['5v5','6v6','7v7','8v8','Custom'].map((t) => <option key={t}>{t}</option>)}
            </select>
          </label>
          <label className="block"><span className="text-xs text-gray-500">Max players</span><input className={inputCls} type="number" value={form.maxPlayers} onChange={(e) => setForm({ ...form, maxPlayers: e.target.value })} /></label>
          <label className="block"><span className="text-xs text-gray-500">Min players</span><input className={inputCls} type="number" value={form.minPlayers} onChange={(e) => setForm({ ...form, minPlayers: e.target.value })} /></label>
          <label className="block"><span className="text-xs text-gray-500">Venue</span><input className={inputCls} value={form.venueName} onChange={(e) => setForm({ ...form, venueName: e.target.value })} /></label>
          <label className="block"><span className="text-xs text-gray-500">Venue address</span><input className={inputCls} value={form.venueAddress} onChange={(e) => setForm({ ...form, venueAddress: e.target.value })} /></label>
          <label className="block"><span className="text-xs text-gray-500">Turf fee (৳)</span><input className={inputCls} type="number" value={form.totalTurfFee} onChange={(e) => setForm({ ...form, totalTurfFee: e.target.value })} /></label>
          <label className="block"><span className="text-xs text-gray-500">Guest fee (৳)</span><input className={inputCls} type="number" value={form.guestFee} onChange={(e) => setForm({ ...form, guestFee: e.target.value })} /></label>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} disabled={busy} onClick={submit}>{busy ? 'Creating…' : 'Create match'}</button>
        </div>
      </div>
    </div>
  );
}

function EditMatchModal({ match, members, onClose, onDone, onToast }: { match: Match; members: AdminMember[]; onClose: () => void; onDone: () => void; onToast: (m: string) => void }) {
  const [status, setStatus] = useState(match.status);
  const [teamAScore, setTeamAScore] = useState(String(match.result?.teamAScore ?? 0));
  const [teamBScore, setTeamBScore] = useState(String(match.result?.teamBScore ?? 0));
  const [notes, setNotes] = useState(match.result?.notes || '');
  const [motm, setMotm] = useState(match.motmWinnerMemberId || '');
  const [title, setTitle] = useState(match.title);
  const [date, setDate] = useState(match.date);
  const [startTime, setStartTime] = useState(match.startTime || '');
  const [endTime, setEndTime] = useState(match.endTime || '');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    const body: Record<string, unknown> = {
      title, date, startTime, endTime, status,
      motmWinnerMemberId: motm || undefined,
    };
    if (['completed', 'awaiting_report'].includes(status)) {
      body.result = {
        teamAScore: Number(teamAScore), teamBScore: Number(teamBScore),
        statsRecorded: status === 'completed',
        notes,
      };
    }
    const res = await api(`/api/admin/matches/${match.id}`, { method: 'PATCH', body: JSON.stringify(body) });
    setBusy(false);
    if (res.ok) { onToast('Match updated'); onDone(); } else onToast(res.error || 'Update failed');
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#12181f] p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between"><h3 className="text-lg font-black">Edit match</h3><button onClick={onClose} className="text-gray-500 hover:text-white">×</button></div>
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 block"><span className="text-xs text-gray-500">Title</span><input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} /></label>
          <label className="block"><span className="text-xs text-gray-500">Date</span><input className={inputCls} type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
          <label className="block"><span className="text-xs text-gray-500">Start / End</span><div className="flex gap-2"><input className={inputCls} type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} /><input className={inputCls} type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} /></div></label>
          <label className="block"><span className="text-xs text-gray-500">Status</span>
            <select className={inputCls} value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </label>
          <label className="block"><span className="text-xs text-gray-500">MOTM</span>
            <select className={inputCls} value={motm} onChange={(e) => setMotm(e.target.value)}>
              <option value="">— none —</option>
              {members.map((m) => <option key={m.id} value={m.id}>{m.displayName}</option>)}
            </select>
          </label>
          {['completed', 'awaiting_report'].includes(status) && (
            <>
              <label className="block"><span className="text-xs text-gray-500">Score A</span><input className={inputCls} type="number" value={teamAScore} onChange={(e) => setTeamAScore(e.target.value)} /></label>
              <label className="block"><span className="text-xs text-gray-500">Score B</span><input className={inputCls} type="number" value={teamBScore} onChange={(e) => setTeamBScore(e.target.value)} /></label>
              <label className="col-span-2 block"><span className="text-xs text-gray-500">Notes</span><input className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
            </>
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} disabled={busy} onClick={submit}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}